import Button from '@ui/buttons/button/button';
import Checkbox from '@ui/checkbox/checkbox';
import Container from '@ui/container/container';
import { ICONS } from '@ui/icons/types';
import OptionalComponent from '@ui/optional-component/optional-component';
import Popover from '@ui/popover/popover';
import Text from '@ui/text/text/text';
import { useAppSelector } from '@/store/hooks';
import { selectPasscodeLists } from '@/store/slices/passcode-lists/selectors';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';

type AudiencesFilterByPasscodeListsParams = {
  isPasscodeList?: boolean;
  selectPasscodeList: (value: number) => void;
  selectAllPasscodeLists: () => void;
  filterPasscodeLists: number[];
  placeholder?: string;
};

const AudiencesFilterByPasscodeLists = ({
  isPasscodeList,
  selectPasscodeList,
  selectAllPasscodeLists,
  filterPasscodeLists,
  placeholder = 'Sort by list',
}: AudiencesFilterByPasscodeListsParams) => {
  const passcodeLists = useAppSelector(selectPasscodeLists);

  const getSelectedString = () => {
    let selectedString = '';
    if (filterPasscodeLists.length === 0) {
      selectedString = placeholder;
    } else if (
      passcodeLists.every((list) => !!list.passcode_list && filterPasscodeLists.includes(list?.passcode_list))
    ) {
      selectedString = 'All lists';
    } else if (filterPasscodeLists.length === 1) {
      selectedString =
        passcodeLists.find((list) => list.passcode_list === filterPasscodeLists[0])?.name || '1 list selected';
    } else if (filterPasscodeLists.length > 1) {
      selectedString = `${filterPasscodeLists.length} lists selected`;
    }

    return selectedString;
  };

  return (
    <OptionalComponent display={!isPasscodeList}>
      <Popover
        containerClassName="flex fd-column ai-center"
        contentClassName="margin-t-8 radius-16"
        buttonBgColor="transparent"
        width="164px"
        maxHeight="250px"
        cycleWithArrowKeys
        renderTriggerButton={({ open }) => {
          return (
            <Button
              clear
              hoverBackgroundColor="none"
              backgroundColor="transparent"
              outlineOnFocus={false}
              className="padding-h-10 padding-l-4 flex jc-space-between ai-center"
              width="164px"
              height="32px"
              borderRadius="16px"
              border={`${open ? '1px solid var(--gray-100)' : '1px solid var(--gray-400)'}`}
              hoverBorder="1px solid var(--gray-100)"
              padding="0 12px"
            >
              <Text
                className="flex fd-row jc-space-between ai-center P2 padding-r-4"
                color="var(--gray-100)"
                height="16px"
                whiteSpace="normal"
                clamp={1}
                fontSize="12px"
                backgroundColor="transparent"
              >
                {getSelectedString()}
              </Text>

              <RotatableIcon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} rotate={open ? '180deg' : '0'} />
            </Button>
          );
        }}
      >
        <Container className="radius-16" borderStyle="none" maxHeight="calc(100vh - 250px)">
          {' '}
          {/* -250px accounts for header height and a bit of spacing at the bottom */}
          <Button
            clear
            className="flex jc-space-between ai-center gap-8"
            backgroundColor="transparent"
            hoverBackgroundColor="var(--gray-700)"
            focusBackgroundColor="var(--gray-700)"
            width="164px"
            onClick={selectAllPasscodeLists}
            padding="9px"
          >
            <Container style={{ pointerEvents: 'none' }}>
              <Checkbox
                size={16}
                checked={passcodeLists.every(
                  (list) => !!list.passcode_list && filterPasscodeLists.includes(list?.passcode_list)
                )}
                // the wrapper button will handle the trigger, we just use checkbox for UI
                disabled
                disabledOpacity={1}
                value={-1}
                selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
                unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
              />
            </Container>
            <Text width="124px" fontSize="12px" whiteSpace="normal" clamp={1}>
              All lists
            </Text>
          </Button>
          {passcodeLists?.map((list) => {
            return (
              <Button
                clear
                className="flex jc-space-between ai-center gap-8"
                key={list.passcode_list}
                backgroundColor="transparent"
                hoverBackgroundColor="var(--gray-700)"
                focusBackgroundColor="var(--gray-700)"
                width="164px"
                onClick={() => selectPasscodeList(list.passcode_list || 0)}
                padding="9px"
              >
                <Container style={{ pointerEvents: 'none' }}>
                  <Checkbox
                    size={16}
                    checked={list?.passcode_list !== undefined && filterPasscodeLists.includes(list?.passcode_list)}
                    // the wrapper button will handle the trigger, we just use checkbox for UI
                    disabled
                    disabledOpacity={1}
                    value={list?.passcode_list || 0}
                    selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
                    unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
                  />
                </Container>
                <Text width="124px" fontSize="12px" whiteSpace="normal" clamp={1}>
                  {list.name}
                </Text>
              </Button>
            );
          })}
        </Container>
      </Popover>
    </OptionalComponent>
  );
};

export default AudiencesFilterByPasscodeLists;
